import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Building2, CheckCircle2, Loader2, RefreshCw, Clock, Copy, Check,
} from 'lucide-react';
import { API } from '@/config';

export const PendingTransfersPanel = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmingId, setConfirmingId] = useState(null);
  const [copied, setCopied] = useState('');

  const fetchPending = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/payments/pending-transfers`);
      setTransactions(data.transactions || []);
    } catch (e) {
      console.error('Fetch pending transfers failed', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPending();
  }, []);

  const confirmTransfer = async (id) => {
    if (!window.confirm('Επιβεβαίωση πληρωμής; Ο πελάτης θα λάβει ειδοποίηση.')) return;
    setConfirmingId(id);
    try {
      await axios.post(`${API}/admin/payments/${id}/confirm-transfer`);
      await fetchPending();
    } catch (e) {
      console.error('Confirm failed', e);
      alert('Αποτυχία επιβεβαίωσης');
    } finally {
      setConfirmingId(null);
    }
  };

  const copyText = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(''), 2000);
    } catch (e) {
      console.error(e);
    }
  };

  const formatDate = (iso) => {
    try { return new Date(iso).toLocaleString('el-GR'); } catch (e) { return iso; }
  };

  return (
    <div className="space-y-4" data-testid="pending-transfers-panel">
      <div className="bg-navy-100/50 rounded-xl border border-slate-700/50 p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Building2 className="w-6 h-6 text-gold" />
            Εκκρεμείς Τραπεζικές Μεταφορές
            {transactions.length > 0 && (
              <span className="ml-2 bg-amber-500 text-navy-200 text-xs font-bold px-2 py-0.5 rounded-full">
                {transactions.length}
              </span>
            )}
          </h2>
          <button
            onClick={fetchPending}
            disabled={loading}
            className="px-3 py-1.5 bg-gold/10 text-gold border border-gold/30 rounded-lg hover:bg-gold/20 text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="pending-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Ανανέωση
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 text-gold animate-spin" />
        </div>
      ) : transactions.length === 0 ? (
        <div className="bg-navy-100/50 rounded-xl border border-slate-700/50 p-12 text-center">
          <Building2 className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400">Δεν υπάρχουν εκκρεμείς μεταφορές</p>
          <p className="text-xs text-slate-500 mt-2">Όταν κάποιος πελάτης ξεκινήσει τραπεζική μεταφορά θα εμφανιστεί εδώ</p>
        </div>
      ) : (
        <div className="space-y-3">
          {transactions.map((t) => (
            <div
              key={t.id}
              className="bg-navy-100/50 rounded-xl border border-amber-500/20 p-5"
              data-testid={`pending-tx-${t.id}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <code className="text-lg font-mono font-bold text-gold tracking-wider">
                      {t.reference_code}
                    </code>
                    <button
                      onClick={() => copyText(t.reference_code, t.id)}
                      className="p-1 hover:bg-slate-700/50 rounded"
                    >
                      {copied === t.id ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5 text-slate-400" />}
                    </button>
                    <span className="px-2 py-0.5 text-xs bg-amber-500/15 text-amber-400 border border-amber-500/30 rounded-full inline-flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Awaiting transfer
                    </span>
                  </div>
                  <p className="text-slate-400 text-xs">{formatDate(t.created_at)}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-white">{Number(t.total_amount).toFixed(2)} €</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3 text-sm">
                <div className="text-slate-300">
                  <span className="text-xs text-slate-500 uppercase tracking-wider mr-2">Πελάτης:</span>
                  <span className="font-medium">{t.username || '—'}</span>
                </div>
                <div className="text-slate-300">
                  <span className="text-xs text-slate-500 uppercase tracking-wider mr-2">Πακέτο:</span>
                  <span className="font-medium">{t.package_name}</span>
                </div>
              </div>

              <button
                onClick={() => confirmTransfer(t.id)}
                disabled={confirmingId === t.id}
                className="w-full sm:w-auto px-5 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold rounded-lg hover:from-emerald-500 hover:to-green-500 disabled:opacity-50 inline-flex items-center justify-center gap-2 text-sm"
                data-testid={`confirm-tx-btn-${t.id}`}
              >
                {confirmingId === t.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4" />
                )}
                Επιβεβαίωση Πληρωμής
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PendingTransfersPanel;
